import type { PromoCandidate } from '@supply-mover-2-0/shared';
import { ResilientImage } from '@supply-mover-2-0/web-ui';
import { useNavigate } from 'react-router-dom';

/**
 * Shared visual building block for storefront promo widgets. The image
 * is the WHOLE visual — headline, discount value, CTA, brand
 * styling all live in the artwork itself (best-in-class promo
 * cards on Weee / Whole Foods / Target work this way too). React
 * just makes the tile clickable and adds a hover lift.
 *
 * The promo's `headline` / `subheadline` / `ctaText` text fields are
 * intentionally NOT rendered here — they're still surfaced on the
 * `/promos/:id` detail page (which has a more text-heavy layout
 * where typography control + accessibility matter more) and remain
 * available for future email / SMS campaigns.
 *
 * Click-through resolves the promo's `ctaLinkType` + `ctaLinkValue`:
 *   - NONE (default) → /promos/:id   (the built-in detail page that
 *                      lists the items participating in the promo;
 *                      login-gated like the rest of the post-auth
 *                      storefront so the customer's price/discount
 *                      eligibility can be shown correctly)
 *   - CATEGORY       → /catalog?category=<value>
 *   - ITEM           → /product/<value>
 *   - ITEM_GROUP     → /catalog?itemGroup=<value>
 *   - EXTERNAL_URL   → window.open(value, '_blank')
 *
 * The promo detail fallback means a tile is *always* clickable — admins
 * never have to configure CTA fields just to make a promo navigable.
 *
 * `size` switches between visual densities — `hero` for full-width
 * banner placements, `feature` for one-up / two-up, `compact` for
 * grid / carousel cells.
 */
export function PromoTile({
	promo,
	size = 'feature',
	density = 'standard',
	onLikelyStaleUrl,
}: {
	promo: PromoCandidate & {
		ctaLinkType?: 'NONE' | 'CATEGORY' | 'ITEM' | 'ITEM_GROUP' | 'EXTERNAL_URL';
		ctaLinkValue?: string | null;
		imagesJson?: {
			bannerUrl?: string | null;
			bannerUrl4x3?: string | null;
			bannerUrlSquare?: string | null;
			bannerStripUrl?: string | null;
		} | null;
	};
	size?: 'hero' | 'feature' | 'slate' | 'compact' | 'strip';
	/**
	 * Density preset. `standard` keeps the established aspect ratios.
	 * `compact` shortens the mobile aspect (~30–40% less vertical space)
	 * while keeping desktop unchanged — for tenants whose customers
	 * prefer a denser-mobile layout (Weee/Instacart patterns). Picked
	 * per widget instance via `widgetConfig.density`.
	 */
	density?: 'standard' | 'compact';
	/**
	 * Invoked once when the banner image fails to load (typically a
	 * stale presigned URL). Caller should refetch whatever query
	 * produced this promo so a freshly-signed banner URL is delivered.
	 */
	onLikelyStaleUrl?: () => void;
}): JSX.Element {
	const navigate = useNavigate();

	const ctaType = promo.ctaLinkType ?? 'NONE';
	const ctaValue = promo.ctaLinkValue;
	// Always clickable. NONE routes to the built-in detail page;
	// configured types go to their target. Only an explicit non-NONE
	// type *without* a value is treated as misconfigured (inert).
	const isClickable = ctaType === 'NONE' || ctaValue != null;

	const handleClick = () => {
		if (ctaType === 'NONE') {
			navigate(`/promos/${promo.id}`);
			return;
		}
		if (ctaValue == null) return;
		switch (ctaType) {
			case 'CATEGORY':
				navigate(`/catalog?category=${encodeURIComponent(ctaValue)}`);
				return;
			case 'ITEM':
				navigate(`/product/${encodeURIComponent(ctaValue)}`);
				return;
			case 'ITEM_GROUP':
				navigate(`/catalog?itemGroup=${encodeURIComponent(ctaValue)}`);
				return;
			case 'EXTERNAL_URL':
				window.open(ctaValue, '_blank', 'noopener,noreferrer');
				return;
			default:
				return;
		}
	};

	// Pick the right banner art for this widget size. Admins upload
	// per-aspect variants (3:1 hero / 4:3 feature / 1:1 square) so
	// each widget gets art composed for its shape — fall back to
	// bannerUrl (3:1) when an aspect isn't uploaded.
	//
	// `compact` (1:1 grid cell): prefer the explicit square upload,
	// then `thumbnailUrl` (the admin's general 1:1 from the Images
	// tab), then crop the 3:1 hero as a last resort.
	//
	// `feature` (1-up / 2-up at half-width) and `slate` (3-up at
	// 1/3-width) both render in 3:1 slots on every breakpoint, so
	// pull the 3:1 source directly. We only fall back to `bannerUrl4x3`
	// when no 3:1 art exists — at that point the blurred-fill
	// background has to absorb the letterbox.
	//
	// `strip` (in-widget section header — promo-with-items, etc.) is
	// strict: ONLY use the dedicated no-text `bannerStripUrl` variant.
	// We don't fall back to the regular `bannerUrl` because that art
	// has the headline + badge baked in, and the 8:1 / 12:1 strip slot
	// crops away ~67% of the source's vertical content — the text
	// always ends up half-clipped and unreadable. Better to render the
	// gradient placeholder (which uses the promo name as text at the
	// strip's actual aspect) than to ship cropped text. Admins who want
	// real strip art upload `bannerStripUrl`.
	const imgs = promo.imagesJson;
	const banner =
		(size === 'compact'
			? (imgs?.bannerUrlSquare ?? imgs?.thumbnailUrl ?? imgs?.bannerUrl)
			: size === 'strip'
				? imgs?.bannerStripUrl
				: size === 'feature' || size === 'slate'
					? (imgs?.bannerUrl ?? imgs?.bannerUrl4x3)
					: imgs?.bannerUrl) ?? null;
	const Wrapper = isClickable ? 'button' : 'div';

	// Banner present → fit-to-cell with a blurred copy as the
	// letterbox background. We use `object-contain` so the entire
	// designed artwork is always visible (admins are uploading
	// pre-composed banners with text + product mix; cropping cuts
	// off headlines or product hero subjects). Any leftover space
	// is filled by a heavily blurred + dimmed copy of the same
	// image, which reads as a tasteful frame rather than an
	// obvious letterbox bar.
	//
	// Aspect-ratio lock per size — without this the wrapper has no
	// intrinsic height, so the CSS grid row stretches to fit the
	// image's natural dimensions. Square uploads (1024×1024) produced
	// 1280-px-tall hero rows on desktop — visually a square block
	// instead of the wide horizontal strip admins expect from a 1×4
	// hero placement. Aspect ratios per slot:
	//   hero    → 4:1   (mainstream ecommerce hero — Walmart 1600×400,
	//                    Shopify 1600×400, Etsy 1200×300; we keep this
	//                    even though gpt-image-2 maxes at 3:1, since
	//                    sizing the slot to a tool limitation is the
	//                    wrong direction. Admin-uploaded 4:1 art fills
	//                    cleanly; AI-generated 3:1 art letterboxes
	//                    against a blurred copy via the fill below)
	//   feature → 3:1 always. The 1-up / 2-up admin-uploaded art is a
	//             3:1 banner; matching the slot exactly on every
	//             breakpoint means no letterbox. An earlier
	//             "4:3 mobile / 3:1 desktop" version was an attempt to
	//             give mobile cards portrait room for the product
	//             hero, but at 390×293 each it produced a wall of
	//             oversized banners — the dashboard read as
	//             promo-spam — and the 3:1 source had to letterbox
	//             into the 4:3 slot anyway, leaving visible blurred
	//             bars top + bottom. 3:1 always is shorter (390×130
	//             on mobile) and matches what real ecommerce mobile
	//             surfaces ship (Weee, Instacart).
	//   slate   → 3:1 always. Same reasoning as feature — the 3-up
	//             header tile-row uses the same 3:1 source art, so a
	//             3:1 slot on every breakpoint fills edge-to-edge.
	//             Mobile stacks single-column so each tile gets full
	//             viewport width × 130; total 3-tile stack lands
	//             around 390px tall instead of the 880px the prior
	//             4:3 mobile slot produced.
	//   compact → 1:1   (matches bannerUrlSquare / thumbnailUrl)
	//
	// Density preset: when `density === 'compact'`, mobile aspects shorten
	// further (5:1 hero/feature/slate, 4:3 compact-size) for ~30–40% less
	// vertical real estate per tile — denser-mobile shopper layouts that
	// fit more content above the fold. Desktop unchanged. Letterboxing
	// against the blurred-fill background absorbs aspect mismatches.
	const standardAspect =
		size === 'hero'
			? 'aspect-[4/1]'
			: size === 'feature'
				? 'aspect-[3/1]'
				: size === 'slate'
					? // promo-three-up tile-row (Featured offers).
						// Mobile: 4:1 — same height as the full-width 4:1
						// hero banners stacked elsewhere on the dashboard,
						// so the peek-carousel reads as a continuation of
						// the same banner cadence rather than a separate,
						// chunkier surface eating real estate above the
						// fold.
						// Desktop: 2:1 — tiles render in a 3-col grid at
						// roughly 1/3-viewport width each (~430px), and a
						// 2:1 frame at that width gives a posterly 215px
						// tile that pairs with surrounding feature/slate
						// widgets. Source 3:1 art object-covers cleanly
						// (vertical crop ~12.5% each at 4:1; horizontal
						// crop ~17% each at 2:1). Display variants place
						// text + subjects in the middle band so both
						// crops are safe.
						'aspect-[2/1]'
					: size === 'strip'
						? // Section-header strip inside promo-with-items. The
							// previous 12:1/16:1 was too aggressive — admin
							// art is composed at 3:1 / 4:1, so an 12:1 frame
							// kept only the middle ~33% of the source's
							// vertical content and chopped the top of the
							// headline + the badge. 8:1 mobile (~49px on a
							// 390 viewport) keeps ~half the source vertically
							// so the "X% OFF" headline stays whole; 12:1
							// desktop (~107px on a 1280 slot) sits between
							// hero (4:1) and the previous 16:1, no clipping.
							'aspect-[16/1] md:aspect-[24/1]'
						: 'aspect-square';
	const compactAspect =
		size === 'hero'
			? 'aspect-[5/1] md:aspect-[4/1]'
			: size === 'feature'
				? 'aspect-[5/1] md:aspect-[3/1]'
				: size === 'slate'
					? 'aspect-[2/1]'
					: size === 'strip'
						? 'aspect-[18/1] md:aspect-[28/1]'
						: 'aspect-[4/3] md:aspect-square';
	const aspectClass = density === 'compact' ? compactAspect : standardAspect;

	if (banner != null) {
		// Every size now uses object-cover — fill the slot edge-to-edge
		// horizontally, vertically center, and let the top/bottom crop
		// take whatever doesn't fit. This replaces the older
		// `object-contain + blurred-fill letterbox` treatment that was
		// added to absorb aspect mismatch between 3:1 source art and
		// wider slots. The letterbox read as faded blurred bars on
		// mobile and felt unfinished, so the design direction now is:
		//   - Source art is composed with all important content in the
		//     middle 30% vertical band (display variant). 3:1 source in
		//     a 4:1 / 5:1 slot crops top + bottom, the band survives.
		//   - Strip slots use a dedicated `bannerStripUrl` no-text
		//     variant where ALL content is decorative (gradients,
		//     blurred subjects) so cropping to 8:1 / 12:1 never loses
		//     readable content.
		//   - Compact 1:1 cells prefer `bannerUrlSquare`; if only the
		//     3:1 source is available, object-cover crops it heavily
		//     to a square, which is acceptable as a fallback.
		return (
			<Wrapper
				type={isClickable ? 'button' : undefined}
				onClick={isClickable ? handleClick : undefined}
				aria-label={promo.name}
				className={`relative w-full ${aspectClass} p-0 rounded-xl overflow-hidden border border-brand-border bg-brand-input ${
					isClickable ? 'cursor-pointer hover:shadow-lg transition-shadow' : ''
				}`}
			>
				<ResilientImage
					src={banner}
					alt={promo.name}
					className="relative block w-full h-full object-cover"
					loading="lazy"
					onLikelyStaleUrl={onLikelyStaleUrl}
					fallback={null}
				/>
			</Wrapper>
		);
	}

	// Fallback when no banner image: render the promo name on a
	// neutral branded gradient so the tile isn't blank.
	//
	// Strip is special: aspect-locked to the same 8:1 / 12:1 the
	// banner-present path uses, NOT a min-h block. The strip slot
	// fundamentally can't host much content (the surrounding widget
	// header is the real headline anyway), so we render a thin gradient
	// band that sits in the row exactly where the no-text strip art
	// would sit. The promo name is centred + truncated so it doesn't
	// overflow the band.
	//
	// All other sizes keep min-h-* + larger padding because they're
	// meant to show the promo name prominently when art is missing.
	const isStripFallback = size === 'strip';
	const sizeClasses = isStripFallback
		? 'px-3 py-1'
		: size === 'hero'
			? 'p-6 md:p-8'
			: size === 'feature' || size === 'slate'
				? 'p-5'
				: 'p-4';
	const minHeight = isStripFallback
		? ''
		: size === 'hero'
			? 'min-h-[200px] md:min-h-[260px]'
			: size === 'feature'
				? 'min-h-[160px]'
				: size === 'slate'
					? 'min-h-[150px]'
					: 'min-h-[140px]';
	const headlineSize = isStripFallback
		? 'text-xs md:text-sm'
		: size === 'hero'
			? 'text-2xl md:text-3xl'
			: size === 'feature' || size === 'slate'
				? 'text-lg md:text-xl'
				: 'text-base';

	return (
		<Wrapper
			type={isClickable ? 'button' : undefined}
			onClick={isClickable ? handleClick : undefined}
			aria-label={promo.name}
			className={`relative w-full ${
				isStripFallback ? `${aspectClass} flex items-center` : 'h-full'
			} text-left rounded-xl overflow-hidden border border-brand-border ${sizeClasses} ${minHeight} ${
				isClickable ? 'cursor-pointer hover:shadow-lg transition-shadow' : ''
			}`}
			style={{ background: 'linear-gradient(135deg, #fbbf24, #f59e0b)' }}
		>
			<p
				className={`relative z-10 font-bold ${headlineSize} text-white drop-shadow ${isStripFallback ? 'truncate' : ''}`}
			>
				{promo.name}
			</p>
		</Wrapper>
	);
}
